import { baseApi } from "@/services/api";

export type LivePlayStatus =
  | "playing"
  | "completed"
  | "paused"
  | "stopped"
  | "stale"
  | "unknown";

export type LivePlayRow = {
  key: string;
  eventId: string;
  deviceId: string;
  deviceName: string;
  androidId?: string;
  brandId?: string;
  brandName?: string;
  masterAccountId: string;
  masterAccountName: string;
  clientName: string;
  locationId: string;
  locationName: string;
  zoneId: string;
  zoneName: string;
  songId: string;
  songTitle: string;
  artist: string;
  channelId: string;
  channel: string;
  channelName: string;
  playedAt: string;
  occurredAt: string;
  updatedAt: string;
  duration: string;
  durationMs: number | null;
  status: LivePlayStatus;
  synthetic: boolean;
  source: string;
};

export type LivePlayMetrics = {
  activeDevices: number;
  nowPlaying: number;
  uniqueArtists: number;
  activeChannels: number;
};

export type LivePlayFilters = {
  brandId?: string;
  clientId?: string;
  locationId?: string;
  zoneId?: string;
  deviceId?: string;
  song?: string;
  artist?: string;
  channel?: string;
  date?: string;
};

export type LivePlayFilterOption = { id: string; name: string };
export type LivePlayLocationOption = LivePlayFilterOption & {
  clientId?: string;
  brandId?: string;
};
export type LivePlayZoneOption = LivePlayFilterOption & {
  locationId?: string;
  clientId?: string;
  brandId?: string;
};
export type LivePlayDeviceOption = LivePlayFilterOption & {
  serialNumber?: string;
  clientId?: string;
  locationId?: string;
  zoneId?: string;
  brandId?: string;
};

export type LivePlayFilterOptions = {
  brands: LivePlayFilterOption[];
  clients: LivePlayFilterOption[];
  locations: LivePlayLocationOption[];
  zones: LivePlayZoneOption[];
  devices: LivePlayDeviceOption[];
  songs: string[];
  artists: string[];
  channels: string[];
};

function buildQuery(filters: LivePlayFilters = {}): string {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function buildLivePlaysPath(filters: LivePlayFilters = {}): string {
  return `v1/sa/device-fleet/live-plays${buildQuery(filters)}`;
}

export function buildLivePlaysFilterOptionsPath(filters: LivePlayFilters = {}): string {
  return `v1/sa/device-fleet/live-plays/filter-options${buildQuery(filters)}`;
}

export function buildLivePlaysSyntheticPath(): string {
  return "v1/sa/device-fleet/live-plays/synthetic";
}

const apiWithTags = baseApi.enhanceEndpoints({});

export const livePlaysApi = apiWithTags.injectEndpoints({
  endpoints: (builder) => ({
    getLivePlays: builder.query<
      {
        items: LivePlayRow[];
        metrics: LivePlayMetrics;
        generatedAt?: string;
        stage?: string;
      },
      LivePlayFilters
    >({
      query: (filters) => buildLivePlaysPath(filters),
      // Poll for near-real-time without browser PubNub fan-out.
      keepUnusedDataFor: 5,
    }),
    getLivePlayFilterOptions: builder.query<LivePlayFilterOptions, LivePlayFilters>({
      query: (filters) => buildLivePlaysFilterOptionsPath(filters),
    }),
    seedSyntheticLivePlay: builder.mutation<
      { synthetic: boolean; eventId: string; deviceId: string; warning?: string },
      Record<string, unknown>
    >({
      query: (body) => ({
        url: buildLivePlaysSyntheticPath(),
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useGetLivePlaysQuery,
  useGetLivePlayFilterOptionsQuery,
  useSeedSyntheticLivePlayMutation,
} = livePlaysApi;
